import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import {
  Autocomplete,
  Box,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";

import Button from "../../components/Button";
import { StyledSwitch } from "../../components/Base";
import { getValidPeriods } from "../../api/validPeriods";
import { getPositionsActive } from "../../api/positions";
import { addCustomCourse, getCustomCourseById, updateCustomCourse } from "../../api/courses";
import { BUFFER_ZONES } from "../../constants";
import IdGenerator from "../../utils/IdGenerator";
import { getCustomPositionValidPeriods } from "../../api/positionValidPeriods";
import { formatBufferZone, formatValidPeriod } from "../../services/formatters";
import courseCategoryService from "../../services/courseCategory";

import "./index.scss";

const idGenerator = new IdGenerator();

const CustomCourseAdd = ({ history, match }) => {
  const { t } = useTranslation();
  const { courseId, crewId } = match.params;

  const isEdit = useMemo(() => {
    return !!courseId;
  }, [courseId]);

  const [status, setStatus] = useState("active");
  const [isCrewSpecific, setIsCrewSpecific] = useState(false);

  const [courseName, setCourseName] = useState("");
  const [courseNameErrorText, setGroupNameErrorText] = useState(null);

  const [validPeriods, setValidPeriods] = useState([]);
  const [courseCategories, setCourseCategories] = useState([]);
  const [courseCategoryId, setCourseCategoryId] = useState("");

  const [validPeriodId, setValidPeriodId] = useState("");

  const [validPeriodErrorText, setValidPeriodErrorText] = useState(null);
  const [bufferZone, setBufferZone] = useState(BUFFER_ZONES[0]);

  const [positions, setPositions] = useState([]);
  const [positionValidPeriods, setPositionValidPeriods] = useState([]);

  function handleCourseName(value) {
    setCourseName(value);
    if (!value) return setGroupNameErrorText(t("custom_courses.name_required"));
    setGroupNameErrorText(null);
  }

  function handleValidPeriod(value) {
    setValidPeriodId(value);
    if (!value) return setValidPeriodErrorText(t("custom_courses.valid_period_required"));
    setValidPeriodErrorText(null);
  }

  function addPositionValidPeriod(value, validPeriodId = "") {
    let position = {};
    if (value.id) position = value;

    const positionValidPeriod = {
      id: idGenerator.getGeneratorId(),
      position: position,
      validPeriodId: validPeriodId,
      isValid: null,
    };
    setPositionValidPeriods((oldValue) => [...oldValue, positionValidPeriod]);
  }

  const simplePositions = useMemo(() => {
    return positionValidPeriods.map((positionValidPeriodWrap) => positionValidPeriodWrap.position);
  }, [positionValidPeriods]);

  const filteredPositions = useMemo(() => {
    return positions.filter(
      (item) =>
        simplePositions.findIndex((selectedItem) => {
          if (selectedItem) return selectedItem.id === item.id;
          return false;
        }) === -1,
    );
  }, [positions, simplePositions]);

  function handleSelectPosition(value, id) {
    // Handle removing text in autocomplete
    let position = value;
    if (!value) position = {};

    const index = positionValidPeriods.findIndex((item) => item.id === id);
    setPositionValidPeriods((oldValue) => {
      const newPositionValidPeriods = [...oldValue];
      newPositionValidPeriods[index].position = position;
      return newPositionValidPeriods;
    });
  }
  function handleSelectPositionPeriod(validPeriodId, id) {
    const index = positionValidPeriods.findIndex((item) => item.id === id);
    setPositionValidPeriods((oldValue) => {
      const newPositionValidPeriods = [...oldValue];
      newPositionValidPeriods[index].validPeriodId = validPeriodId;
      return newPositionValidPeriods;
    });
  }
  function deletePositionValidPeriod(id) {
    const index = positionValidPeriods.findIndex((item) => item.id === id);
    setPositionValidPeriods((oldValue) => {
      const newPositionValidPeriods = [...oldValue];
      newPositionValidPeriods.splice(index, 1);
      return newPositionValidPeriods;
    });
  }
  async function addEditCourse() {
    if (!courseName) return handleCourseName(courseName);
    if (!validPeriodId) return handleValidPeriod(validPeriodId);
    let isPositionPeriodsValid = true;
    if (positionValidPeriods.length) {
      positionValidPeriods.forEach((positionValidPeriod, index) => {
        if (Object.keys(positionValidPeriod.position).length === 0 || positionValidPeriod.validPeriodId === "") {
          isPositionPeriodsValid = false;
          setPositionValidPeriods((oldValue) => {
            const newPositionValidPeriods = [...oldValue];
            newPositionValidPeriods[index].isValid = false;
            return newPositionValidPeriods;
          });
        } else {
          setPositionValidPeriods((oldValue) => {
            const newPositionValidPeriods = [...oldValue];
            newPositionValidPeriods[index].isValid = true;
            return newPositionValidPeriods;
          });
        }
      });
    }
    if (!isPositionPeriodsValid) return;
    const newPositionValidPeriods = positionValidPeriods.map((positionValidPeriod) => ({
      positionId: positionValidPeriod.position.id,
      validPeriodId: positionValidPeriod.validPeriodId,
    }));
    const newCustomCourse = {
      name: courseName,
      validPeriodId: validPeriodId,
      status: status,
      bufferZone: isValidPeriodExpire ? bufferZone : null,
      positionValidPeriods: newPositionValidPeriods,
      courseCategoryId: courseCategoryId || null,
    };
    if (crewId) {
      newCustomCourse.crewId = crewId;
    } else {
      newCustomCourse.isCrewSpecific = Number(isCrewSpecific);
    }
    let result;
    if (isEdit) {
      result = await updateCustomCourse(courseId, newCustomCourse);
    } else {
      result = await addCustomCourse(newCustomCourse);
    }

    if (result.ok) {
      if (crewId) {
        history.push(`/crew/${crewId}/customCourses`);
      } else {
        history.push(`/courses/custom`);
      }
    }
  }

  const isValidPeriodExpire = useMemo(() => {
    return validPeriodId > 1;
  }, [validPeriodId]);

  useEffect(() => {
    (async () => {
      const newValidPeriods = await getValidPeriods();
      setValidPeriods(newValidPeriods);

      const newPositions = await getPositionsActive();
      setPositions(newPositions);

      const allCourseCategories = courseCategoryService.getOptions();
      setCourseCategories(allCourseCategories);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      if (isEdit) {
        const customCourse = await getCustomCourseById(courseId);
        const positionValidPeriods = await getCustomPositionValidPeriods(courseId);
        const mappedPositionValidPeriods = positionValidPeriods.map((positionValidPeriod) => ({
          position: positionValidPeriod.position,
          validPeriodId: positionValidPeriod.validPeriod.id,
        }));
        setPositionValidPeriods(mappedPositionValidPeriods);

        setCourseName(customCourse.name);
        setStatus(customCourse.status);
        setIsCrewSpecific(!!customCourse.isCrewSpecific);
        setBufferZone(customCourse.buffer ? customCourse.buffer : BUFFER_ZONES[0]);
        handleValidPeriod(customCourse.validPeriodId);
        if (customCourse.courseCategoryId) setCourseCategoryId(customCourse.courseCategoryId);
      }
    })();
  }, [courseId, isEdit]);

  return (
    <>
      <h2>{isEdit ? t("custom_courses.edit_title") : t("custom_courses.add_title")}</h2>

      <Paper variant="outlined" className="add-custom-course" style={{ padding: "1rem 1.5rem" }}>
        {(isEdit && (positions.length || positionValidPeriods.length)) || !isEdit ? (
          <>
            <TextField
              className="add-field"
              variant="outlined"
              size="small"
              label={t("custom_courses.full_name_required_label")}
              value={courseName}
              onChange={(event) => handleCourseName(event.target.value)}
              error={!!courseNameErrorText}
              helperText={courseNameErrorText}
            />
            <FormControl
              variant="outlined"
              size="small"
              error={!!validPeriodErrorText}
              className="select-course-period"
            >
              <InputLabel id="selectCoursePeriod">
                {t("custom_courses.select_default_validity_period_required")}
              </InputLabel>
              <Select
                labelId="selectCoursePeriod"
                label={t("custom_courses.select_default_validity_period_required")}
                value={validPeriodId}
                onChange={(event) => handleValidPeriod(event.target.value)}
              >
                {validPeriods.map((period) => (
                  <MenuItem key={period.id} value={period.id}>
                    {formatValidPeriod(period.validPeriod, t)}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>{validPeriodErrorText}</FormHelperText>
            </FormControl>
            <FormControl variant="outlined" size="small" className="select-course-period">
              <InputLabel id="selectTheme">{t("course.select_theme")}</InputLabel>
              <Select
                labelId="selectTheme"
                label={t("course.select_theme")}
                value={courseCategoryId}
                onChange={(event) => setCourseCategoryId(event.target.value)}
              >
                {courseCategories.map((category) => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.getDisplayName(t)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {isValidPeriodExpire ? (
              <FormControl variant="outlined" size="small" className="select-buffer">
                <InputLabel id="selectBufferZone">{t("course.select_buffer_zone")}</InputLabel>
                <Select
                  className="select-buffer-zone"
                  labelId="selectBufferZone"
                  label={t("course.select_buffer_zone")}
                  value={bufferZone}
                  onChange={(event) => {
                    setBufferZone(event.target.value);
                  }}
                >
                  {BUFFER_ZONES.map((buffer) => (
                    <MenuItem key={buffer} value={buffer}>
                      {formatBufferZone(buffer)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : null}
            <Button className="add-position-button" onClick={addPositionValidPeriod}>
              {t("course.add_position_period")}
            </Button>
            <Box>
              {positionValidPeriods.map((positionValidPeriod, index) => (
                <FormControl
                  key={positionValidPeriod.id}
                  error={positionValidPeriod.isValid === false}
                  className="position-period-row-wrapper"
                >
                  <div className="position-period-row">
                    <Autocomplete
                      className="select-position"
                      size="small"
                      options={filteredPositions}
                      getOptionLabel={(position) => (position.name ? position.name : "")}
                      filterSelectedOptions
                      value={positionValidPeriod.position}
                      onChange={(_, newPosition) => handleSelectPosition(newPosition, positionValidPeriod.id)}
                      renderInput={(params) => (
                        <TextField {...params} variant="outlined" label={`${t("position.select")}*`} size="small" />
                      )}
                    />
                    <FormControl className="select-period" variant="outlined" size="small">
                      <InputLabel id={"selectPositionPeriod" + positionValidPeriod.id}>
                        {t("course.select_period")}
                      </InputLabel>
                      <Select
                        labelId={"selectCoursePeriod" + positionValidPeriod.id}
                        label={`${t("course.select_period")}*`}
                        value={positionValidPeriod.validPeriodId}
                        onChange={(event) => {
                          handleSelectPositionPeriod(event.target.value, positionValidPeriod.id);
                        }}
                      >
                        {validPeriods.map((period) => (
                          <MenuItem key={period.id} value={period.id}>
                            {formatValidPeriod(period.validPeriod, t)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Button
                      title={t("course.remove_position_period")}
                      icon={<ClearIcon />}
                      onClick={() => deletePositionValidPeriod(positionValidPeriod.id)}
                    />
                  </div>
                  <FormHelperText>
                    {positionValidPeriod.isValid === false ? t("custom_courses.position_valid_period_error") : null}
                  </FormHelperText>
                </FormControl>
              ))}
            </Box>
            <div className="change-status">
              <label>{t("status")}</label>
              <StyledSwitch
                title={t("custom_courses.change_status")}
                checked={status === "active"}
                onChange={() => setStatus(status === "active" ? "passive" : "active")}
              />
            </div>
            {!crewId ? (
              <div className="change-status">
                <label>{t("custom_courses.company_specific")}</label>
                <StyledSwitch
                  title={t("custom_courses.change_company_specific_status")}
                  checked={isCrewSpecific}
                  onChange={() => setIsCrewSpecific(!isCrewSpecific)}
                />
              </div>
            ) : null}
            <div>
              <Button title={t("custom_courses.save_action")} onClick={addEditCourse}>
                {isEdit ? t("custom_courses.update") : t("save")}
              </Button>
              <Button title={t("navigate_back")} onClick={() => history.goBack()}>
                {t("cancel")}
              </Button>
            </div>
          </>
        ) : null}
      </Paper>
    </>
  );
};

export default CustomCourseAdd;
